import { memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import ApplicationSourceChip from 'src/components/ApplicationSourceChip';
import Iconify from 'src/components/Iconify';
import IconButton from 'src/components/IconButton';
import ListActionBar from 'src/components/ListActionBar';
import MissingValueText from 'src/components/MissingValueText';
import StatusButton from 'src/components/StatusButton';
import TableAppImage from 'src/components/Table/TableAppImage';
import TableHeadTypography from 'src/components/TableHeadTypography';
import TableContainer from 'src/components/Table/TableContainer';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import HighlightedText from 'src/components/HighlightedText';
import { TableCellTypography } from 'src/components/GenericTableCell';
import useTypographyMediaQuery from 'src/hooks/useTypographyMediaQuery';
import { useAppPortfolioVersions, useUpdateAppPortfolioStatus } from 'src/hooks/wdac';
import { PORTAL_ROUTE } from 'src/constants/routes';
import { pagesText } from 'src/constants/pagesText';
import { formatDateShortLocal } from 'src/utils/formatTime';
import parseError from 'src/utils/parseError';
import {
  APPLICATION_TRUST_STATUS,
  getApplicationTrustStatusLabel,
} from 'src/utils/enums/applicationTrustStatus';
import { WDAC_APP_PREVIOUS_PATH } from 'src/pages/wdac-results/tabs/Win32Apps/constants';
import Win32AppStatusModal, {
  getStatusSavedMessage,
} from 'src/pages/wdac-results/tabs/Win32Apps/Win32AppStatusModal';
import { APP_PORTFOLIO_COLUMNS, PORTFOLIO_FILTER_OPTIONS } from './constants';

const CLOSED_STATUS_MODAL = { open: false, version: null };

const ELLIPSIS_SX = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
  maxWidth: '100%',
};

const CHILD_ROW_SX = { backgroundColor: '#3F3F3F' };

const ColumnHeader = ({ label, sortable, sortActive, sortDirection, onSort }) => {
  const { mediumScreen, largeScreen, smallScreen } = useTypographyMediaQuery();

  return (
    <Stack
      direction="row"
      gap={1}
      alignItems="center"
      onClick={sortable ? onSort : undefined}
      sx={{ cursor: sortable ? 'pointer' : 'default' }}>
      <TableHeadTypography
        hasSorting={sortActive}
        mediumScreen={mediumScreen}
        largeScreen={largeScreen}
        smallScreen={smallScreen}>
        {label}
      </TableHeadTypography>
      {sortable ? (
        <Iconify
          icon={
            sortActive
              ? sortDirection === 'asc'
                ? 'mdi:arrow-up'
                : 'mdi:arrow-down'
              : 'mdi:swap-vertical'
          }
          width={14}
          height={14}
          color={sortActive ? '#7DFF92' : '#9CA3AF'}
        />
      ) : null}
    </Stack>
  );
};

const SOURCE_CHIP_SX = { height: 18, fontSize: '10px' };

// An app no import or catalog vouches for is known only from what devices reported, which is why it
// has no source to show.
export const SourceChips = ({ sources }) => (
  <Stack direction="row" gap={0.5} flexWrap="wrap">
    {sources?.length ? (
      sources.map(source => (
        <ApplicationSourceChip key={source} source={source} sx={SOURCE_CHIP_SX} />
      ))
    ) : (
      <Chip
        size="small"
        variant="outlined"
        label="Telemetry only"
        sx={{
          ...SOURCE_CHIP_SX,
          fontWeight: 500,
          color: '#9CA3AF',
          borderColor: 'rgba(156, 163, 175, 0.4)',
          backgroundColor: 'rgba(90, 90, 90, 0.25)',
        }}
      />
    )}
  </Stack>
);

// The versions of one app, including the one representing it in the collapsed row. They reuse the
// group's columns so a group reads as one table, and are not indented.
const VersionRows = ({
  latestGlobalApplicationVersionId,
  statuses,
  sources,
  onOpen,
  onEditStatus,
}) => {
  const { data: versions, isLoading } = useAppPortfolioVersions({
    globalApplicationVersionId: latestGlobalApplicationVersionId,
    statuses,
    sources,
  });

  if (isLoading) {
    return <LoadingRows columnCount={APP_PORTFOLIO_COLUMNS.length} rowCount={2} />;
  }

  if (!versions?.length) {
    return (
      <TableRow sx={CHILD_ROW_SX}>
        <TableCell colSpan={APP_PORTFOLIO_COLUMNS.length} sx={{ py: 0.75 }}>
          <TableCellTypography sx={{ fontSize: '0.68rem', color: '#9CA3AF', m: 0 }}>
            No versions of this application in this environment
          </TableCellTypography>
        </TableCell>
      </TableRow>
    );
  }

  return versions.map(version => (
    <TableRow
      key={version.globalApplicationVersionId}
      hover
      onClick={() => onOpen(version.globalApplicationVersionId)}
      sx={{ ...CHILD_ROW_SX, cursor: 'pointer' }}>
      <TableCell sx={{ py: 0.75 }}>
        <TableAppImage width={28} height={28} name={version.displayName} imgSrc={version.iconUrl} />
      </TableCell>
      <TableCell sx={{ py: 0.75, minWidth: 0 }}>
        <TableCellTypography sx={{ fontSize: '0.72rem', m: 0, display: 'block', ...ELLIPSIS_SX }}>
          {version.displayName || <MissingValueText label="name" />}
        </TableCellTypography>
      </TableCell>
      <TableCell sx={{ py: 0.75, minWidth: 0 }}>
        <TableCellTypography sx={{ fontSize: '0.72rem', m: 0, display: 'block', ...ELLIPSIS_SX }}>
          {version.publisher || <MissingValueText label="publisher" />}
        </TableCellTypography>
      </TableCell>
      <TableCell sx={{ py: 0.75, minWidth: 0 }}>
        <TableCellTypography sx={{ fontSize: '0.72rem', m: 0, display: 'block', ...ELLIPSIS_SX }}>
          {version.displayVersion || <MissingValueText label="version" />}
        </TableCellTypography>
      </TableCell>
      <TableCell sx={{ py: 0.75 }}>
        <TableCellTypography sx={{ fontSize: '0.72rem', m: 0 }}>
          {formatDateShortLocal(version.trustUpdatedOn) || '—'}
        </TableCellTypography>
      </TableCell>
      <TableCell sx={{ py: 0.75 }}>
        <SourceChips sources={version.sources} />
      </TableCell>
      <TableCell sx={{ py: 0.75 }}>
        <StatusButton
          status={version.status}
          size="small"
          sx={{ fontSize: '10px', padding: '1px 6px', minWidth: 0 }}
          onClick={event => {
            event.stopPropagation();
            onEditStatus(version);
          }}>
          {getApplicationTrustStatusLabel(version.status)}
        </StatusButton>
      </TableCell>
    </TableRow>
  ));
};

const PortfolioTable = ({
  isLoading,
  results,
  sentryRef,
  isFetchingNextPage,
  totalCount,
  searchValue,
  onSearchChange,
  filters,
  onFilterChange,
  sort,
  onSortChange,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  const [statusModal, setStatusModal] = useState(CLOSED_STATUS_MODAL);

  const { mutateAsync: updateStatus, isLoading: statusLoading } = useUpdateAppPortfolioStatus();

  // The version is kept on close so the dialog body does not empty out mid transition.
  const closeStatusModal = useCallback(
    () => setStatusModal(previous => ({ ...previous, open: false })),
    [],
  );

  const openStatusModal = useCallback(version => setStatusModal({ open: true, version }), []);

  const statusAppId = statusModal.version?.globalApplicationVersionId;

  const openAppDetails = useCallback(
    globalApplicationVersionId => {
      sessionStorage.setItem(
        WDAC_APP_PREVIOUS_PATH,
        `${window.location.pathname}${window.location.search}`,
      );
      navigate(`${PORTAL_ROUTE}/wdac-apps/${globalApplicationVersionId}/general`);
    },
    [navigate],
  );

  // A trust finishes on the general tab of the app, where what it built is shown.
  const saveStatus = useCallback(
    async values => {
      try {
        await updateStatus({ globalApplicationVersionId: statusAppId, ...values });
        closeStatusModal();
        enqueueSnackbar(getStatusSavedMessage(values), {
          variant: 'success',
          autoHideDuration: 3000,
        });
        if (values.status === APPLICATION_TRUST_STATUS.Trusted) {
          openAppDetails(statusAppId);
        }
      } catch (error) {
        enqueueSnackbar(parseError(error) || pagesText.somethingWentWrong, {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    },
    [closeStatusModal, enqueueSnackbar, openAppDetails, statusAppId, updateStatus],
  );

  const handleSort = useCallback(
    column => {
      if (!column.sortable || !column.sortKey) {
        return;
      }
      onSortChange(
        sort.column === column.sortKey
          ? { column: column.sortKey, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
          : { column: column.sortKey, direction: 'asc' },
      );
    },
    [onSortChange, sort],
  );

  const toggleExpanded = useCallback(id => {
    setExpandedRows(previous => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const trimmedSearch = (searchValue || '').trim();

  return (
    <Box sx={{ position: 'relative' }}>
      <ListActionBar
        onSearch={onSearchChange}
        defaultSearch={searchValue}
        onResetSearch={() => onSearchChange('')}
        searchPlaceholder="Search applications..."
        filters={filters}
        onFilterChange={onFilterChange}
        filterOptions={PORTFOLIO_FILTER_OPTIONS}
        filterChips
        filterButtonCount={totalCount}
      />

      <TableContainer roundedTop styles={{ maxHeight: `calc(100vh - 300px)` }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': { fontSize: '0.7rem' },
          }}>
          <colgroup>
            {APP_PORTFOLIO_COLUMNS.map(column => (
              <col key={column.key} style={{ width: column.width }} />
            ))}
          </colgroup>
          <TableHead sx={{ backgroundColor: '#3f3f3f', borderRadius: 0 }}>
            <TableRow>
              {APP_PORTFOLIO_COLUMNS.map(column => (
                <TableCell key={column.key} sx={{ top: 0, p: '8px 16px', verticalAlign: 'middle' }}>
                  <ColumnHeader
                    label={column.label}
                    sortable={!!column.sortable}
                    sortActive={sort.column === column.sortKey}
                    sortDirection={sort.direction}
                    onSort={() => handleSort(column)}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
            {isLoading ? (
              <LoadingRows columnCount={APP_PORTFOLIO_COLUMNS.length} rowCount={10} />
            ) : (
              <>
                {(results || []).map((row, index) => {
                  const expanded = expandedRows.has(row.latestGlobalApplicationVersionId);

                  return [
                    <TableRow
                      key={row.latestGlobalApplicationVersionId}
                      ref={index === results.length - 1 ? sentryRef : null}
                      hover
                      onClick={() => toggleExpanded(row.latestGlobalApplicationVersionId)}
                      sx={{ cursor: 'pointer' }}>
                      <TableCell sx={{ py: 1 }}>
                        <TableAppImage
                          width={40}
                          height={40}
                          name={row.displayName}
                          imgSrc={row.iconUrl}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1, minWidth: 0 }}>
                        <TableCellTypography
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            m: 0,
                            display: 'block',
                            ...ELLIPSIS_SX,
                          }}>
                          <HighlightedText
                            text={row.displayName}
                            searchValue={trimmedSearch}
                            fallback={<MissingValueText label="name" />}
                          />
                        </TableCellTypography>
                      </TableCell>
                      <TableCell sx={{ py: 1, minWidth: 0 }}>
                        <TableCellTypography
                          sx={{ fontSize: '0.72rem', m: 0, display: 'block', ...ELLIPSIS_SX }}>
                          <HighlightedText
                            text={row.publisher}
                            searchValue={trimmedSearch}
                            fallback={<MissingValueText label="publisher" />}
                          />
                        </TableCellTypography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <TableCellTypography
                          sx={{ fontSize: '0.72rem', m: 0, display: 'block', ...ELLIPSIS_SX }}>
                          {row.latestDisplayVersion || <MissingValueText label="version" />}
                        </TableCellTypography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <TableCellTypography sx={{ fontSize: '0.72rem', m: 0 }}>
                          {formatDateShortLocal(row.trustUpdatedOn) || '—'}
                        </TableCellTypography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <SourceChips sources={row.sources} />
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Stack direction="row" alignItems="center" gap={0.25}>
                          <Typography
                            variant="caption"
                            sx={{ color: '#D1D5DB', fontWeight: 600, fontSize: '0.72rem' }}>
                            {Number(row.versionsCount || 0).toLocaleString()}
                          </Typography>
                          <IconButton
                            onClick={event => {
                              event.stopPropagation();
                              toggleExpanded(row.latestGlobalApplicationVersionId);
                            }}
                            sx={{ '&:hover': { backgroundColor: 'unset' } }}>
                            <Iconify
                              icon={expanded ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                              width={20}
                              height={20}
                              color="#9CA3AF"
                            />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>,
                    expanded ? (
                      <VersionRows
                        key={`${row.latestGlobalApplicationVersionId}-versions`}
                        latestGlobalApplicationVersionId={row.latestGlobalApplicationVersionId}
                        statuses={filters.statuses}
                        sources={filters.sources}
                        onOpen={openAppDetails}
                        onEditStatus={openStatusModal}
                      />
                    ) : null,
                  ];
                })}
                {isFetchingNextPage && (
                  <LoadingRows columnCount={APP_PORTFOLIO_COLUMNS.length} rowCount={3} />
                )}
                {!isFetchingNextPage && (results || []).length === 0 && (
                  <NoDataCell colSpan={APP_PORTFOLIO_COLUMNS.length}>
                    {pagesText.noDataFound}
                  </NoDataCell>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Win32AppStatusModal
        open={statusModal.open}
        onClose={closeStatusModal}
        globalApplicationVersionId={statusAppId}
        summary={[statusModal.version?.displayName, statusModal.version?.displayVersion]
          .filter(Boolean)
          .join(' ')}
        currentStatus={statusModal.version?.status || ''}
        currentDate={statusModal.version?.trustUpdatedOn || null}
        onSave={saveStatus}
        loading={statusLoading}
      />
    </Box>
  );
};

export default memo(PortfolioTable);
